import { LogHelper } from 'app/core/helpers/log.helper';
import { AlertService } from './../../../../shared/alert/alert.service';
import { Component, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import {
  PatientRegistrationAction,
  PatientRegistrationActionTakenRequest,
  PatientRegistrationListViewModel,
  PatientRegistrationViewModel
} from 'app/core/models/patient.model';
import { ResponsePage } from 'app/core/models/search-page.model';
import { PatientService } from 'app/core/services/patient.service';
import { ModalComponent } from 'app/shared/modal/modal.component';
import { interval, Subscription, take, takeWhile } from "rxjs";
import { ConfirmActionModalComponent } from "../../../../shared/confirm-action-modal/confirm-action-modal.component";
import { PatientAddModalComponent } from 'app/shared/patient-add-modal/patient-add-modal.component';
import { TrialAutocomplete } from 'app/shared/trial-autocomplete/trial-autocomplete.model';
import { LanguageCultureOptions } from "../../../../core/constants/language-culture-options";
import { SplitButtonAction } from "../../../../shared/split-button/split-button.component";

@Component({
  selector: 'app-patient-registrations',
  templateUrl: './patient-registrations.component.html',
  styleUrls: ['./patient-registrations.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PatientRegistrationsComponent implements OnDestroy {
  @ViewChild('patientRegistrationDetails') patientRegistrationDetails: ModalComponent;
  @ViewChild('confirmRejectPatientRegistrationModal') confirmRejectPatientRegistrationModal: ConfirmActionModalComponent;
  @ViewChild(PatientAddModalComponent) patientAddModal: PatientAddModalComponent;
  keywords: string;
  orderBy: number = 0;
  state: string = null;
  result: ResponsePage<PatientRegistrationListViewModel>;
  patientRegistration: PatientRegistrationViewModel;
  subscription: Subscription;
  patientCreatedSubscription: Subscription;
  rejectionPatientId: string | null = null;
  patientLanguage: string = null;
  viewedRegistrationIds: string[] = [];

  documents: { label: string, url: string }[] = [];

  private siteSetSubscription: Subscription;

  constructor(private patientService: PatientService, private alertService: AlertService) { }

  updateFilter(keywords: string, orderBy: number = 0, state: string = null) {
    this.keywords = keywords === '' ? null : keywords;
    this.orderBy = orderBy;
    this.state = null;
  }

  loadPatientRegistrations(page: number, keywords: string, orderBy: number, state: string = null) {
    this.state = state;
    this.patientService.getPatientRegistrations(page ?? 1, keywords, orderBy, state).subscribe({
      next: result => {
        this.result = result
      },
      error: err => {
        LogHelper.log(err);
        this.alertService.showWarningAlert('Unable to load patient registrations!');
      }
    })
  }

  onPatientRegistrationClicked(patientRegistrationId: string) {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.subscription = this.patientService.getPatientRegistration(patientRegistrationId).subscribe({
      next: response => {
        this.documents = [];
        this.patientRegistration = response;
        this.patientLanguage = this.resolveLanguageDisplay(response.language);

        if (response.signedRegistrationFilename) {
          this.documents.push({
            label: 'Signed Patient Registration Form',
            url: response.signedRegistrationFilename
          });
        }

        if (response.bankDetailsFilename) {
          this.documents.push({
            label: 'Bank Details Form',
            url: response.bankDetailsFilename
          });
        }

        if (response.miscFilenames) {
          const miscDocs = response.miscFilenames.split(';');

          for (let i = 0; i < miscDocs.length; i++) {
            const doc = miscDocs[i];
            const label = miscDocs.length > 1
              ? `Miscellaneous Document (${i + 1})`
              : 'Miscellaneous Document';

            this.documents.push({
              label: label,
              url: doc
            });
          }
        }

        if (!this.viewedRegistrationIds.includes(response.id)) {
          this.viewedRegistrationIds.push(response.id);
        }
        this.patientRegistrationDetails.show()
      },
      error: error => {
        LogHelper.log(error);
        this.alertService.showWarningAlert("Failed to get patient registration details.");
      }
    })
  }

  onReject(patientRegistrationId: string): void {
    this.rejectionPatientId = patientRegistrationId;
    this.confirmRejectPatientRegistrationModal.showConfirmation(patientRegistrationId);
  }

  onRejectConfirmed(rejectionPatientId: any): void {
    const rejectionRequest: PatientRegistrationActionTakenRequest = {
      patientRegistrationId: rejectionPatientId,
      patientRegistrationAction: PatientRegistrationAction.Rejected
    };

    this.patientService.rejectPatientRegistration(rejectionRequest).subscribe({
      next: () => {
        this.rejectionPatientId = null;
        this.confirmRejectPatientRegistrationModal.hide();
        this.alertService.showSuccessAlert('Patient registration has been rejected.');

        this.loadPatientRegistrations(this.result.currentPage, this.keywords, this.orderBy, this.state);
      },
      error: err => {
        this.rejectionPatientId = null;
        this.alertService.showErrorAlert(err);
      }
    });
  }

  onAcceptRegistration(patientRegistration: PatientRegistrationListViewModel): void {
    this.patientAddModal.show();
    this.patientAddModal.patientRegistrationId = patientRegistration.id;
    this.patientAddModal.trialAutocomplete.onSelectOption({ id: patientRegistration.trialId, label: patientRegistration.trialCode } as TrialAutocomplete);

    if (this.patientCreatedSubscription) {
      this.patientCreatedSubscription.unsubscribe();
    }

    this.patientCreatedSubscription = this.patientAddModal.patientCreated.subscribe(() => {
      this.loadPatientRegistrations(this.result.currentPage, this.keywords, this.orderBy, this.state);
      this.patientAddModal.hide();
    })

    let firstname = patientRegistration.patientFirstName;
    let lastname = patientRegistration.patientLastName;

    if (!firstname && !lastname && patientRegistration.patientName) {
      const nameParts = patientRegistration.patientName.split(' ');
      firstname = nameParts[0];
      lastname = nameParts.slice(1).join(' ');
    }

    const comments = patientRegistration.comments ?? '';
    const notes = patientRegistration.cardNumber
      ? `Card Number: ${patientRegistration.cardNumber}, ${comments}`
      : comments;

    this.patientAddModal.form.patchValue({
      trialInfo: {
        trialCode: patientRegistration.trialCode,
        patientCode: patientRegistration.patientNumber,
        site: patientRegistration.siteId
      },
      patientInfo: {
        firstname,
        lastname,
        email: patientRegistration.patientEmail,
        culture: patientRegistration.language
      },
      extraInfo: {
        notes,
        caxtonWalletName: patientRegistration.cardNumber
      }
    });

    if (this.siteSetSubscription) {
      this.siteSetSubscription.unsubscribe();
    }

    this.siteSetSubscription = interval(250).pipe(
      take(20),
      takeWhile(() => {
        const dropdown = this.patientAddModal.siteDropdown;
        const isReady = dropdown.filteredSites.length > 0 && !dropdown.isLoading;

        if (isReady) {
          dropdown.setSite(patientRegistration.siteId);

          this.patientAddModal.form.patchValue({
            patientInfo: {
              culture: patientRegistration.language ?? dropdown.selectedSite?.culture ?? 'en-gb'
            }
          });

          return false;
        }

        return true;
      })
    ).subscribe();
  }

  public isPdf(uploadUrl: string) {
    let parts = uploadUrl.toLowerCase().split('?');
    return parts[0].endsWith('.pdf');
  }

  public getMiscFiles(): string[] {
    if (!this.patientRegistration?.miscFilenames) {
      return [];
    }
    // Split by semicolon to handle multiple files
    const files = this.patientRegistration.miscFilenames
      .split(';')
      .map(filename => filename.trim())
      .filter(filename => filename.length > 0);

    // Sort to show PDF files first
    return files.sort((a, b) => {
      const aIsPdf = this.isPdf(a);
      const bIsPdf = this.isPdf(b);

      if (aIsPdf && !bIsPdf) return -1;
      if (!aIsPdf && bIsPdf) return 1;
      return 0;
    });
  }

  getActions(registration: PatientRegistrationListViewModel): SplitButtonAction[] {
    const isViewed = this.viewedRegistrationIds.includes(registration.id);
    const isPending = registration.state === 'Pending';

    const viewAction: SplitButtonAction = { label: 'View', dropdownLabel: 'View Registration', value: 'view', variant: 'info', dropdownVariant: 'secondary', dropdownFaIcon: 'far fa-search' };
    const acceptAction: SplitButtonAction = { label: 'Accept', dropdownLabel: 'Accept Registration', value: 'accept', variant: 'success', dropdownVariant: 'success', dropdownFaIcon: 'far fa-check' };
    const rejectAction: SplitButtonAction = { label: 'Reject', dropdownLabel: 'Reject Registration', value: 'reject', variant: 'danger',dropdownVariant: 'danger', dropdownFaIcon: 'far fa-ban' };

    const actions: SplitButtonAction[] = [];

    if (isViewed && isPending) {
      actions.push({ ...acceptAction, default: true }, viewAction, rejectAction);
    } else if (isPending) {
      actions.push({ ...viewAction, default: true }, acceptAction, rejectAction);
    } else {
      actions.push({ ...viewAction, default: true });
    }

    return actions;
  }

  onSplitAction(action: string, registration: PatientRegistrationListViewModel): void {
    switch (action) {
      case 'view':
        this.onPatientRegistrationClicked(registration.id);
        break;
      case 'accept':
        this.onAcceptRegistration(registration);
        break;
      case 'reject':
        this.onReject(registration.id);
        break;
    }
  }

  private resolveLanguageDisplay(language: string): string {
    if (!language || !language.includes('-')) {
      return language;
    }

    const match = LanguageCultureOptions.all().find(c => c.cultureInfoCode.toLowerCase() === language.toLowerCase());
    return match ? `${match.language} (${match.cultureInfoCode.toLowerCase()})` : language;
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    if (this.patientCreatedSubscription) {
      this.patientCreatedSubscription.unsubscribe();
    }

    if (this.siteSetSubscription) {
      this.siteSetSubscription.unsubscribe();
    }
  }
}
